<script setup lang="ts">
/**
 * Навигация приложения: на десктопе — рейл с иконками разделов плюс панель
 * подразделов, на mobile — полноэкранная шторка по бургеру.
 *
 * Панелей два независимых слоя: закреплённая (группа текущего маршрута, стоит в
 * потоке и раздвигает контент) и preview (группа, по чьей иконке кликнули, —
 * выезжает из-под рейла поверх контента и закреплённой панели).
 */
import { onClickOutside, useEventListener } from '@vueuse/core';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import type { MenuItem } from '../model/menu-types';

import { authMenuAccess, menuItems } from '../config/menu-items';
import { getVisibleMenuItems } from '../model/menu-visibility';
import { findActiveGroupId, findMenuGroup } from '../model/sidebar-panel-state';
import { useSidebar } from '../model/use-sidebar';
import AppSidebarMobileMenu from './AppSidebarMobileMenu.vue';
import AppSidebarPanel from './AppSidebarPanel.vue';
import AppSidebarRail from './AppSidebarRail.vue';

const {
  isMobile,
  pinnedGroupId,
  previewGroupId,
  isPanelCollapsed,
  sidebarOffset,
  closePreview,
  togglePanelCollapsed,
  handleRailGroupClick,
} = useSidebar();

const route = useRoute();

const asideRef = ref<HTMLElement | null>(null);

const visibleMenuItems = computed<MenuItem[]>(() => {
  return getVisibleMenuItems(menuItems, authMenuAccess());
});

const pinnedGroup = computed(() => findMenuGroup(visibleMenuItems.value, pinnedGroupId.value));
const previewGroup = computed(() => findMenuGroup(visibleMenuItems.value, previewGroupId.value));

// Единственный писатель pinnedGroupId: панель всегда выведена из текущего маршрута.
watch(
  () => route.name,
  (name) => {
    pinnedGroupId.value = findActiveGroupId(visibleMenuItems.value, typeof name === 'string' ? name : '');
  },
  { immediate: true },
);

// Переход по ссылке из preview-панели закрывает оверлей: маршрут уже сменился,
// дальше панель показывает новая закреплённая группа. При пересоздании страницы
// старый watcher может не выполниться до unmount, поэтому сбрасываем и при setup.
watch(() => route.fullPath, closePreview, { immediate: true });

/**
 * Если preview закрывается потому, что его группа стала закреплённой (переход по
 * ссылке внутри него), уезжать ему некуда: под ним уже стоит такая же панель.
 * Снимаем анимацию выхода, иначе видно «двойника».
 */
const markInstantLeave = (el: Element): void => {
  if (el instanceof HTMLElement && el.dataset.groupId === pinnedGroupId.value) {
    el.classList.add('sidebar-preview--instant');
  }
};

// Возврат фокуса на иконку раздела после закрытия preview с клавиатуры.
const focusGroupButton = (id: string): void => {
  asideRef.value?.querySelector<HTMLElement>(`button[data-group-id="${id}"]`)?.focus();
};

// Клик вне сайдбара закрывает preview.
onClickOutside(asideRef, closePreview);

useEventListener(document, 'keydown', (event: KeyboardEvent) => {
  if (event.key !== 'Escape' || previewGroup.value === null) {
    return;
  }

  const groupId = previewGroup.value.id;

  closePreview();
  focusGroupButton(groupId);
});
</script>

<template>
  <AppSidebarMobileMenu
    v-if="isMobile"
    :items="visibleMenuItems"
  />

  <aside
    v-else
    ref="asideRef"
    data-pull-to-refresh-ignore
    class="sidebar"
  >
    <AppSidebarRail
      :items="visibleMenuItems"
      :active-group-id="pinnedGroupId"
      :open-group-id="previewGroup?.id ?? pinnedGroupId"
      @group-click="handleRailGroupClick"
    />

    <AppSidebarPanel
      v-if="pinnedGroup"
      :group="pinnedGroup"
      :mode="isPanelCollapsed ? 'collapsed' : 'pinned'"
      @toggle="togglePanelCollapsed"
    />

    <Transition
      name="sidebar-preview"
      @before-leave="markInstantLeave"
    >
      <AppSidebarPanel
        v-if="previewGroup"
        :key="previewGroup.id"
        :group="previewGroup"
        mode="preview"
        @toggle="closePreview"
      />
    </Transition>
  </aside>
</template>

<style scoped>
.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  /* 100vh в iOS Safari — «большой» viewport (с прижатыми тулбарами): нижнее меню
     и футер уезжают под панель браузера. 100dvh следует за видимой областью;
     100vh остаётся fallback для браузеров без поддержки dvh. */
  height: 100vh;
  height: 100dvh;
  width: v-bind(sidebarOffset);
  flex-shrink: 0;
  z-index: var(--sidebar-z-index);
}

/* Preview выезжает из-под рейла и уезжает под него; при смене группы старая
   панель уезжает, новая въезжает одновременно. */
.sidebar-preview-enter-active,
.sidebar-preview-leave-active {
  transition: transform var(--sidebar-preview-duration) var(--sidebar-preview-easing);
}

.sidebar-preview-enter-from,
.sidebar-preview-leave-to {
  transform: translateX(-100%);
}

.sidebar-preview--instant.sidebar-preview-leave-active {
  transition: none;
}
</style>
